import React, { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate, Link } from 'react-router-dom';
import {
  LayoutDashboard,
  Calendar,
  ClipboardList,
  Users,
  FileText,
  Receipt,
  BarChart3,
  Settings,
  HelpCircle,
  LogOut,
  Menu,
  X,
  Activity,
  BellRing,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { ToastProvider } from '../../contexts/ToastContext';
import { BrandingProvider, useBranding } from '../../contexts/BrandingContext';

const INTER_FONT_HREF = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap';

const useInterFont = () => {
  useEffect(() => {
    if (document.querySelector(`link[href="${INTER_FONT_HREF}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = INTER_FONT_HREF;
    document.head.appendChild(link);
  }, []);
};

const BrandIdentity: React.FC<{ onClick?: () => void }> = ({ onClick }) => {
  const { practice_name, logo_url } = useBranding();
  return (
    <Link
      to="/admin"
      onClick={onClick}
      className="flex items-center gap-2.5 group focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent/50 rounded-md min-w-0"
      aria-label={`${practice_name} — Dashboard`}
    >
      {logo_url && (
        <img
          src={logo_url}
          alt=""
          className="h-9 w-9 object-contain transition-transform duration-300 ease-out group-hover:scale-[1.03] drop-shadow-[0_2px_4px_rgba(0,0,0,0.25)]"
        />
      )}
      <span
        className={`text-white font-semibold tracking-tight truncate ${
          logo_url ? 'text-sm max-w-[140px]' : 'text-base max-w-[200px]'
        }`}
      >
        {practice_name}
      </span>
    </Link>
  );
};

interface NavItem {
  name: string;
  href: string;
  icon: LucideIcon;
  current: boolean;
}

const NavLink: React.FC<{ item: NavItem; onClick?: () => void }> = ({ item, onClick }) => {
  const Icon = item.icon;
  return (
    <Link
      to={item.href}
      onClick={onClick}
      aria-current={item.current ? 'page' : undefined}
      className={`group relative flex items-center gap-3 px-3 py-2 text-[13px] font-medium rounded-md transition-colors duration-200 cursor-pointer ${
        item.current
          ? 'bg-brand-accent/15 text-brand-primary'
          : 'text-brand-text-muted hover:bg-brand-surface-2 hover:text-brand-primary'
      }`}
    >
      <span
        aria-hidden
        className={`absolute left-0 top-1/2 -translate-y-1/2 w-[3px] rounded-r-full bg-brand-accent transition-all duration-200 ${
          item.current ? 'h-7 opacity-100' : 'h-0 opacity-0 group-hover:h-4 group-hover:opacity-60'
        }`}
      />
      <Icon
        aria-hidden
        className={`h-[18px] w-[18px] shrink-0 transition-colors duration-200 ${
          item.current ? 'text-brand-primary' : 'text-brand-text-muted group-hover:text-brand-primary'
        }`}
        strokeWidth={1.75}
      />
      <span className="truncate flex-1">{item.name}</span>
    </Link>
  );
};

const AdminLayoutInner: React.FC = () => {
  useInterFont();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const { practice_name, header_tagline } = useBranding();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const navigation: NavItem[] = [
    { name: 'Dashboard', href: '/admin', icon: LayoutDashboard, current: location.pathname === '/admin' },
    { name: 'Kalender', href: '/admin/calendar', icon: Calendar, current: location.pathname === '/admin/calendar' },
    { name: 'Termine', href: '/admin/appointments', icon: ClipboardList, current: location.pathname === '/admin/appointments' },
    { name: 'Erinnerungen', href: '/admin/reminders', icon: BellRing, current: location.pathname === '/admin/reminders' },
    { name: 'Patienten', href: '/admin/patients', icon: Users, current: location.pathname.startsWith('/admin/patients') },
    { name: 'Erhebungsbögen', href: '/admin/intake', icon: FileText, current: location.pathname === '/admin/intake' },
    { name: 'Belege', href: '/admin/invoices', icon: Receipt, current: location.pathname.startsWith('/admin/invoices') },
    { name: 'Auswertungen', href: '/admin/reports', icon: BarChart3, current: location.pathname === '/admin/reports' },
    { name: 'Event Log', href: '/admin/event-log', icon: Activity, current: location.pathname === '/admin/event-log' },
    { name: 'Einstellungen', href: '/admin/settings', icon: Settings, current: location.pathname === '/admin/settings' },
    { name: 'Hilfe', href: '/admin/help', icon: HelpCircle, current: location.pathname === '/admin/help' },
  ];

  return (
    <div data-admin-shell className="bg-brand-surface-2 min-h-screen font-admin text-[13px] text-brand-text">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setSidebarOpen(false)}></div>
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-brand-surface">
          <div className="relative flex items-center justify-between h-16 px-4 bg-gradient-to-br from-brand-primary to-brand-primary-hover shadow-md after:absolute after:inset-x-0 after:bottom-0 after:h-px after:bg-gradient-to-r after:from-transparent after:via-brand-accent/40 after:to-transparent">
            <BrandIdentity onClick={() => setSidebarOpen(false)} />
            <button
              onClick={() => setSidebarOpen(false)}
              aria-label="Sidebar schließen"
              className="text-white/85 hover:text-white transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40 rounded-md p-1"
            >
              <X className="h-5 w-5" strokeWidth={2} />
            </button>
          </div>
          <nav className="flex-1 px-3 py-4">
            <ul className="space-y-1">
              {navigation.map((item) => (
                <li key={item.name}>
                  <NavLink item={item} onClick={() => setSidebarOpen(false)} />
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-40 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-brand-surface border-r border-brand-border">
          <div className="relative flex items-center h-16 px-4 bg-gradient-to-br from-brand-primary to-brand-primary-hover shadow-md after:absolute after:inset-x-0 after:bottom-0 after:h-px after:bg-gradient-to-r after:from-transparent after:via-brand-accent/40 after:to-transparent">
            <BrandIdentity />
          </div>
          <nav className="flex-1 px-3 py-4">
            <ul className="space-y-1">
              {navigation.map((item) => (
                <li key={item.name}>
                  <NavLink item={item} />
                </li>
              ))}
            </ul>
          </nav>
          <div className="px-4 py-3 border-t border-brand-border">
            <p className="text-[10px] uppercase tracking-[0.15em] text-brand-text-muted font-semibold">{practice_name} · v1.0</p>
          </div>
        </div>
      </div>

      <div className="lg:pl-64 flex flex-col min-h-screen">
        {/* Top header */}
        <div className="sticky top-0 z-30 bg-brand-surface shadow-sm border-b border-brand-border">
          <div className="flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
            <div className="flex items-center">
              <button
                type="button"
                className="lg:hidden -ml-0.5 -mt-0.5 inline-flex h-11 w-11 items-center justify-center rounded-md text-brand-text-muted hover:text-brand-text hover:bg-brand-surface-2 transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40"
                onClick={() => setSidebarOpen(true)}
                aria-label="Sidebar öffnen"
              >
                <Menu className="h-5 w-5" strokeWidth={2} />
              </button>

              {header_tagline && (
                <div className="ml-4 lg:ml-0 hidden sm:flex items-center">
                  <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-brand-text-muted leading-none whitespace-nowrap">
                    {header_tagline}
                  </span>
                </div>
              )}
            </div>

            <div className="flex items-center gap-3">
              <div className="hidden md:flex flex-col items-end leading-tight">
                <span className="text-[13px] font-semibold text-brand-text">
                  {user?.firstName} {user?.lastName}
                </span>
                <span className="text-[11px] text-brand-text-muted">{user?.email}</span>
              </div>
              <div
                className="h-9 w-9 rounded-full bg-gradient-to-br from-brand-primary to-brand-primary-hover flex items-center justify-center ring-2 ring-brand-accent/40 ring-offset-2 ring-offset-brand-surface shadow-sm"
                aria-hidden
              >
                <span className="text-sm font-semibold text-white">
                  {user?.firstName?.[0]}{user?.lastName?.[0]}
                </span>
              </div>
              <button
                onClick={handleLogout}
                title="Abmelden"
                aria-label="Abmelden"
                className="inline-flex h-9 w-9 items-center justify-center rounded-md text-brand-text-muted hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10 dark:hover:text-rose-400 transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-500/40"
              >
                <LogOut className="h-[18px] w-[18px]" strokeWidth={1.75} />
              </button>
            </div>
          </div>
        </div>

        {/* Main content */}
        <main className="flex-1 py-6">
          <div className="px-4 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>

        <footer className="mt-auto border-t border-brand-border bg-brand-surface/60 backdrop-blur-sm">
          <div className="px-4 sm:px-6 lg:px-8 py-3 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-[11px] text-brand-text-muted">
            <span>Powered by <span className="font-semibold tracking-wide text-brand-text">LEANOFY</span></span>
            <span aria-hidden className="text-brand-border">·</span>
            <span>
              Support &amp; Anfragen:{' '}
              <a
                href="https://www.leanofy.de"
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40 rounded-sm"
              >
                www.leanofy.de
              </a>
            </span>
          </div>
        </footer>
      </div>
    </div>
  );
};

const AdminLayout: React.FC = () => (
  <ToastProvider>
    <BrandingProvider>
      <AdminLayoutInner />
    </BrandingProvider>
  </ToastProvider>
);

export default AdminLayout;
